import { JstDate } from '../../../core/time/business-time';
import { BusinessClock } from '../../../core/time/business-clock.service';
import { Injectable, inject } from '@angular/core';
import { AuthStateService } from '../../../core/services/auth-state.service';
import { MemberStatusApi, MemberStatusRecord, MemberStatusRequestError } from '../data-access/member-status-api.service';
import { MemberStatusStorage } from '../data-access/member-status-storage.service';
import { MemberStatusCommand, MemberStatusOperation, MemberStatusTarget, historyEndDate, statusBlocked, statusBusy, statusCommandValid, statusUpdateBody } from '../domain/member-status';

const UNKNOWN = '更新結果を確認できません。再送せず、会員情報と関連履歴・鍵の状態を確認してください。';
const PARTIAL = '会員の状態変更は完了しましたが、履歴停止の完了を確認できません。会員更新を再送せず、履歴を確認してください。';
const STALE = '状態変更は完了しました。一覧を再取得してください。';

@Injectable({ providedIn: 'root' })
export class MemberStatusWorkflow {
  private clock = inject(BusinessClock);
  private api = inject(MemberStatusApi);
  private auth = inject(AuthStateService);
  private storage = inject(MemberStatusStorage);
  private entries = new Map<string, MemberStatusOperation>();
  recoveryError = '';
  private get owner(): number | null {
    const user = this.auth.currentUser;
    return user?.token && Number.isInteger(user.id) && Number(user.id) > 0 ? Number(user.id) : null;
  }
  private key(owner: number, studioId: number, memberId: number): string { return `${owner}:${studioId}:${memberId}`; }
  private set(value: MemberStatusOperation): void { this.entries.set(this.key(value.owner, value.studioId, value.memberId), value); }
  operation(studioId: number, memberId: number): MemberStatusOperation | undefined {
    return this.owner ? this.entries.get(this.key(this.owner, studioId, memberId)) : undefined;
  }
  forStudio(studioId: number): MemberStatusOperation[] {
    return [...this.entries.values()].filter(value => value.owner === this.owner && value.studioId === studioId && statusBlocked(value));
  }
  enterStudio(studioId: number): void {
    const owner = this.owner;
    this.recoveryError = '';
    if (!owner) return;
    try {
      for (const memberId of this.storage.members(owner, studioId)) {
        if (this.operation(studioId, memberId)) continue;
        let restored: MemberStatusOperation;
        try {
          const value = this.storage.read(owner, studioId, memberId);
          if (!value) continue;
          const completed = ['saved', 'saved-stale', 'refreshing'].includes(value.phase);
          restored = { ...value, phase: completed ? 'saved-stale' : value.memberConfirmed ? 'partial' : 'unknown',
            message: completed ? STALE : value.memberConfirmed ? PARTIAL : UNKNOWN };
        } catch {
          restored = { owner, studioId, memberId, status: 0, phase: 'unknown', memberConfirmed: false, historyConfirmed: false,
            message: '回復記録が不正です。再送せず、管理者が実際の結果を確認してください。' };
        }
        this.set(restored);
      }
    } catch { this.recoveryError = '回復記録を読み取れません。保存領域を利用できる状態にしてから画面を開き直してください。'; }
  }
  async execute(target: MemberStatusTarget, command: MemberStatusCommand): Promise<{ completed: boolean; members?: MemberStatusRecord[] }> {
    const owner = this.owner;
    if (!owner || this.recoveryError || statusBlocked(this.operation(target.studioId, target.memberId))
      || !statusCommandValid(target, command, this.auth.currentUser?.role ?? 99)) return { completed: false };
    const session = this.auth.token;
    const sameSession = () => this.owner === owner && this.auth.token === session;
    let value: MemberStatusOperation = { owner, studioId: target.studioId, memberId: target.memberId, status: command.status,
      phase: 'checking', memberConfirmed: false, historyConfirmed: false, message: '現在の状態を確認しています。' };
    this.set(value);
    let sent = false;
    try {
      const member = await this.api.read(target.memberId);
      if (!await this.clock.synchronize(true)) {
        this.set({ ...value, phase: 'failed', message: this.clock.message() });
        return { completed: false };
      }
      const now = new JstDate();
      if (!sameSession() || !statusCommandValid(target, command, this.auth.currentUser?.role ?? 99)) throw new Error('owner-changed');
      if (member.status !== target.currentStatus || member.studioId !== target.studioId) {
        this.set({ ...value, phase: 'failed', message: '会員の状態または店舗が変更されています。一覧を再取得してから操作してください。' });
        return { completed: false };
      }
      value = { ...value, phase: 'saving', message: '状態を変更しています。' };
      this.storage.write(value); // If durable recovery cannot be recorded, do not send.
      this.set(value);
      sent = true;
      if (command.status === 3) await this.api.remove(target.memberId);
      else await this.api.update(statusUpdateBody(target.memberId, command, now));
      value = { ...value, memberConfirmed: true };
      if (command.status === 1 || command.status === 2) {
        value = { ...value, phase: 'history', message: '状態変更は完了しました。履歴を停止しています。' };
        this.storage.write(value);
        this.set(value);
        if (!sameSession()) throw new Error('owner-changed-after-write');
        await this.api.disableHistory(target.memberId, historyEndDate(now));
        value = { ...value, historyConfirmed: true };
      }
      value = { ...value, phase: 'saved-stale', message: STALE };
      this.set(value);
      this.storage.write(value);
      if (!sameSession()) return { completed: true };
      const members = await this.refresh(value);
      return { completed: true, members };
    } catch (error) {
      if (value.phase === 'saved-stale') {
        this.set(value);
        return { completed: true };
      }
      const authFailure = error instanceof MemberStatusRequestError && ['authentication', 'forbidden'].includes(error.reason);
      const phase = value.memberConfirmed ? 'partial' : sent && !authFailure ? 'unknown' : 'failed';
      const message = phase === 'partial' ? PARTIAL : phase === 'unknown' ? UNKNOWN : authFailure
        ? '認証または権限を確認できません。再ログイン後にもう一度確認してください。' : '変更前の確認または回復記録の保存に失敗しました。入力を残しています。';
      value = { ...value, phase, message };
      this.set(value);
      try { if (phase === 'failed') this.storage.clear(value); else this.storage.write(value); } catch { /* The pending marker, if written, remains conservative. */ }
      return { completed: false };
    }
  }
  async refresh(operation: MemberStatusOperation): Promise<MemberStatusRecord[] | undefined> {
    if (operation.owner !== this.owner || statusBusy(this.operation(operation.studioId, operation.memberId))) return undefined;
    const current = this.operation(operation.studioId, operation.memberId) ?? operation;
    const session = this.auth.token;
    const completed = current.phase === 'saved-stale' || current.phase === 'saved';
    this.set({ ...current, phase: 'refreshing', message: '一覧を再取得しています。' });
    try {
      const members = await this.api.list(operation.studioId);
      if (completed) {
        this.storage.clear(current);
        this.set({ ...current, phase: 'saved', message: '状態を変更しました。' });
      } else this.set(current);
      return operation.owner === this.owner && session === this.auth.token ? members : undefined;
    } catch {
      this.set({ ...current, message: `${current.message} 一覧の取得に失敗しました。` });
      return undefined;
    }
  }
  acknowledge(operation: MemberStatusOperation): boolean {
    if (operation.owner !== this.owner || !['unknown', 'partial'].includes(this.operation(operation.studioId, operation.memberId)?.phase ?? '')) return false;
    try {
      this.storage.clear(operation);
      this.entries.delete(this.key(operation.owner, operation.studioId, operation.memberId));
      return true;
    } catch { return false; }
  }
}
